import api from "./api";

/**
 * Kakao Login (카카오 로그인).
 *
 * The button only appears once the backend reports the keys are configured, so
 * this ships dormant and lights up the moment KAKAO_REST_API_KEY is set on
 * Cloud Run — no redeploy of the storefront needed.
 */

let cached = null;

export async function getKakaoStatus() {
  if (cached) return cached;
  try {
    const { data } = await api.get("/users/kakao/status");
    cached = data;
  } catch {
    cached = { enabled: false };
  }
  return cached;
}

/** Where Kakao sends the customer back to. Must match the console exactly. */
export const kakaoRedirectUri = () => `${window.location.origin}/auth/kakao/callback`;

/**
 * Send the customer to Kakao's consent screen.
 * `next` is where we return them afterwards; a referral code rides along so a
 * Kakao signup still credits whoever invited them.
 */
export async function startKakaoLogin({ next = "/", referralCode = null } = {}) {
  const cfg = await getKakaoStatus();
  if (!cfg.enabled || !cfg.client_id) return false;

  sessionStorage.setItem("kakaoNext", next);
  if (referralCode) sessionStorage.setItem("kakaoReferral", referralCode);

  const params = new URLSearchParams({
    client_id: cfg.client_id,
    redirect_uri: cfg.redirect_uri || kakaoRedirectUri(),
    response_type: "code",
  });
  // Only send a scope when one has actually been approved — see the backend's
  // /kakao/status comment.
  if (cfg.scopes) params.append("scope", cfg.scopes);

  window.location.href = `https://kauth.kakao.com/oauth/authorize?${params.toString()}`;
  return true;
}

/**
 * Exchange the code for our own session.
 *
 * Kakao accounts can be phone-only, so the backend may answer "I need an email
 * first" — that is not an error, it is a second step. `email` and `signupToken`
 * are how the caller completes it.
 */
export async function completeKakaoLogin({ code, email = null, signupToken = null }) {
  const cfg = await getKakaoStatus();
  const body = {
    redirect_uri: cfg.redirect_uri || kakaoRedirectUri(),
    referral_code: sessionStorage.getItem("kakaoReferral") || undefined,
  };
  if (signupToken) {
    body.signup_token = signupToken;
    body.email = email;
  } else {
    body.code = code;
  }

  try {
    const { data } = await api.post("/users/kakao-login", body);
    sessionStorage.removeItem("kakaoReferral");
    return { ok: true, ...data };
  } catch (err) {
    const d = err.response?.data || {};
    if (d.needs_email) {
      return { ok: false, needsEmail: true, signupToken: d.signup_token, message: d.message };
    }
    if (d.needs_password_link) {
      return { ok: false, needsPasswordLink: true, message: d.message };
    }
    return { ok: false, message: d.message || "Kakao login failed" };
  }
}
